import { createSelector } from "reselect";
import {
  selectCurrentWorkspaceIdOrSlug,
  selectDefaultWorkspaceId,
} from "@features/application/app-selectors";
import { workspacesApi } from "@features/workspaces/workspaces-api";

/**
 * Selector that retrieves all workspaces available to the current user.
 * Returns an empty array if no workspaces are loaded or if the API request failed.
 */
export const selectUserWorkspaces = createSelector(
  workspacesApi.endpoints.getWorkspaces.select(undefined),
  (result) => result.data,
);

/**
 * Selector that determines the current active workspace based on a priority system.
 * This selector implements a three-tier priority system for workspace selection:
 *
 * Priority 1: Workspace from URL parameter (highest priority)
 * - Matches workspace by ID or slug from the current route
 * - Allows users to directly navigate to a specific workspace
 *
 * Priority 2: Default workspace from user settings (medium priority)
 * - Falls back to the user's configured default workspace
 * - Provides consistent workspace selection across sessions
 *
 * Priority 3: First available workspace (the lowest priority)
 * - Selects the first workspace from the user's workspace list
 * - Ensures users always have a workspace selected when possible
 *
 * @returns {WorkspaceEntity | undefined | null} The selected workspace is or undefined if no workspaces are available or null if no workspace is found or no access to workspaces is granted.
 */
export const selectCurrentWorkspace = createSelector(
  [selectCurrentWorkspaceIdOrSlug, selectDefaultWorkspaceId, selectUserWorkspaces],
  (urlWorkspaceIdOrSlug, defaultWorkspaceId, workspaces) => {
    // Early return if no workspaces are available or the URL workspace is explicitly undefined (not loaded)
    if (!workspaces || urlWorkspaceIdOrSlug === undefined) {
      return undefined;
    }

    if (workspaces.length === 0) {
      return null;
    }

    // Priority 1: Workspace from URL parameter
    // Checks if there's a workspace ID or slug in the URL and find matching workspace
    if (urlWorkspaceIdOrSlug) {
      const workspaceFromUrl = workspaces.find(
        (w) => w.id === urlWorkspaceIdOrSlug || w.slug === urlWorkspaceIdOrSlug,
      );
      if (workspaceFromUrl) {
        return workspaceFromUrl;
      }
      if (!workspaceFromUrl) {
        return null;
      }
    }

    // Priority 2: Default workspace from user settings
    // Fall back to user's configured default workspace if URL workspace not found
    if (defaultWorkspaceId) {
      const defaultWorkspace = workspaces.find((w) => w.id === defaultWorkspaceId);
      if (defaultWorkspace) {
        return defaultWorkspace;
      }
    }

    // Priority 3: First available workspace
    // Last resort - select the first workspace to ensure user always has an active workspace
    return workspaces[0];
  },
);
